import { Keypair } from '@solana/web3.js';
import * as bs58 from 'bs58';
import CryptoJS from 'crypto-js';

// Convert Uint8Array to string for storage
export function uint8ArrayToString(array: Uint8Array): string {
    return bs58.encode(array);
}

// Convert string back to Uint8Array
export function stringToUint8Array(str: string): Uint8Array {
    return bs58.decode(str);
}

// Encrypt private key with AES encryption
export function encryptPrivateKey(secretKey: Uint8Array, passphrase: string): string {
    const wordArray = CryptoJS.lib.WordArray.create(secretKey as any);
    const encrypted = CryptoJS.AES.encrypt(wordArray.toString(CryptoJS.enc.Base64), passphrase).toString();
    return encrypted;
}

// Decrypt private key
export function decryptPrivateKey(encrypted: string, passphrase: string): Uint8Array {
    try {
        // Validate input
        if (!encrypted || typeof encrypted !== 'string') {
            throw new Error('Invalid encrypted key format');
        }

        if (!passphrase || typeof passphrase !== 'string') {
            throw new Error('Invalid passphrase format');
        }

        // Attempt decryption
        const decrypted = CryptoJS.AES.decrypt(encrypted, passphrase);
        
        // Check if decryption was successful
        if (!decrypted || !decrypted.toString(CryptoJS.enc.Utf8)) {
            throw new Error('Decryption failed - invalid encrypted data or passphrase');
        }

        const base64String = decrypted.toString(CryptoJS.enc.Utf8);
        
        // Validate base64 string
        if (!base64String || !/^[A-Za-z0-9+/=]+$/.test(base64String)) {
            throw new Error('Invalid decrypted data format');
        }

        const wordArray = CryptoJS.enc.Base64.parse(base64String);

        // Validate word array
        if (!wordArray || !wordArray.sigBytes || wordArray.sigBytes !== 64) {
            throw new Error('Invalid key length after decryption');
        }

        // Convert WordArray to Uint8Array
        const uint8 = new Uint8Array(wordArray.sigBytes);
        for (let i = 0; i < wordArray.sigBytes; i++) {
            uint8[i] = (wordArray.words[i >>> 2] >>> (24 - (i % 4) * 8)) & 0xff;
        }

        // Validate final Uint8Array
        if (uint8.length !== 64) {
            throw new Error('Invalid key length in final output');
        }

        return uint8;
    } catch (error) {
        console.error('Error decrypting private key:', error);
        if (error instanceof Error) {
            throw new Error(`Failed to decrypt private key: ${error.message}`);
        }
        throw new Error('Failed to decrypt private key. The passphrase may be incorrect or the key is corrupted.');
    }
}

// Generate a new keypair - completely browser compatible
export function generateNewKeypair(): { keypair: Keypair, mnemonic: string } {
    try {
        // Simply use Solana's built-in keypair generation
        const keypair = Keypair.generate();

        // Generate a fake mnemonic for display/backup purposes
        // Note: This is not a real BIP39 mnemonic, just a representation
        const randomWords = [
            'abandon', 'ability', 'able', 'about', 'above', 'absent', 'absorb', 'abstract', 'absurd', 'abuse',
            'access', 'accident', 'account', 'accuse', 'achieve', 'acid', 'acoustic', 'acquire', 'across', 'act',
            'action', 'actor', 'actress', 'actual', 'adapt', 'add', 'addict', 'address', 'adjust', 'admit',
            'adult', 'advance', 'advice', 'aerobic', 'affair', 'afford', 'afraid', 'again', 'age', 'agent',
            'agree', 'ahead', 'aim', 'air', 'airport', 'aisle', 'alarm', 'album', 'alcohol', 'alert'
        ];

        // Create a deterministic "phrase" based on the keypair's public key
        // This isn't a real mnemonic but provides a human-readable representation
        const pubKeyBytes = keypair.publicKey.toBytes();
        const fakeWords = [];

        for (let i = 0; i < 12; i++) {
            // Use bytes from the public key to select words
            const index = pubKeyBytes[i % pubKeyBytes.length] % randomWords.length;
            fakeWords.push(randomWords[index]);
        }

        const pseudoMnemonic = fakeWords.join(' ');

        return { keypair, mnemonic: pseudoMnemonic };
    } catch (error) {
        console.error("Error generating keypair:", error);
        // Fallback
        const keypair = Keypair.generate();
        return {
            keypair,
            mnemonic: "Error creating mnemonic. Please use the backup string instead."
        };
    }
}

// Create a backup string that can be saved by the user
export function createKeypairBackup(keypair: Keypair): string {
    return uint8ArrayToString(keypair.secretKey);
}

// Restore a keypair from a backup string
export function restoreKeypairFromBackup(backupString: string): Keypair {
    try {
        const secretKey = stringToUint8Array(backupString);
        return Keypair.fromSecretKey(secretKey);
    } catch (error) {
        console.error('Error restoring keypair from backup:', error);
        throw new Error('Invalid backup string');
    }
}

// Generate a secure passphrase based on the user's wallet address
export function generatePassphrase(walletAddress: string, salt: string): string {
    return CryptoJS.PBKDF2(walletAddress, salt, {
        keySize: 512 / 32,
        iterations: 10000
    }).toString();
}

// NEW: Hash user password for storage
export function hashWalletPassword(password: string, salt: string): string {
    return CryptoJS.PBKDF2(password, salt, {
        keySize: 512 / 32,
        iterations: 100000
    }).toString();
}

// NEW: Verify the wallet password against stored hash
export function verifyWalletPassword(password: string, storedHash: string, salt: string): boolean {
    const hashedPassword = hashWalletPassword(password, salt);
    return hashedPassword === storedHash;
}

// NEW: Generate a secure random token for password reset
export function generateResetToken(): string {
    const randomBytes = CryptoJS.lib.WordArray.random(32);
    return randomBytes.toString(CryptoJS.enc.Hex);
}

// NEW: Encrypt private key with user password for export
export function encryptPrivateKeyWithPassword(
    privateKeyBackup: string,
    password: string
): string {
    return CryptoJS.AES.encrypt(privateKeyBackup, password).toString();
}

// NEW: Decrypt private key with user password
export function decryptPrivateKeyWithPassword(
    encryptedPrivateKey: string,
    password: string
): string {
    try {
        const decrypted = CryptoJS.AES.decrypt(encryptedPrivateKey, password);
        return decrypted.toString(CryptoJS.enc.Utf8);
    } catch (error) {
        console.error('Error decrypting private key with password:', error);
        throw new Error('Incorrect password. Please try again.');
    }
}
